import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { hook } from '@g4/automation-hooks';
import { Row, Col, Carousel, useMedia } from '../../../components/Boneyard';

import {
  HIGHLIGHTED_DEALS_CAROUSEL_MARKER,
  HIGHLIGHTED_DEALS_DESKTOP_MARKER,
} from '../../../../automationHooks.json';

const propTypes = {
  children: PropTypes.node.isRequired,
  highlightedDealCycleDelay: PropTypes.oneOf(
    PropTypes.string,
    PropTypes.number
  ),
};

const IndicatorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

const Indicator = styled.button.attrs({
  type: 'button',
})`
  height: 6px;
  width: 6px;
  margin: 0 ${({ theme }) => theme.spacing(2)};
  background-color: ${({ isActive, theme }) =>
    isActive
      ? theme.ancillariesColors.indicator.isActive
      : theme.ancillariesColors.indicator.isNotActive};
  border-radius: 5px;
`;

const HighlightedDeals = ({ children, highlightedDealCycleDelay }) => {
  const mq = useMedia();

  const [slideIndex, setSlideIndex] = React.useState(0);

  const childrenArray = React.Children.toArray(children).filter(Boolean);

  const convertToMilliseconds = delay => {
    let seconds = delay;

    if (typeof seconds === 'string') {
      seconds = Number.parseInt(seconds);
    }

    if (Number.isNaN(seconds)) {
      seconds = 0;
    }

    return seconds * 1000;
  };

  const cycleDelay = convertToMilliseconds(highlightedDealCycleDelay);

  // Uses SLIDE_OFFSET to allow slides to align left by default and right for the last slide
  const goToSlide = index => {
    setSlideIndex(index);
  };

  if (mq.gte('lg')) {
    return (
      <Row
        gutter={[30, 30]}
        type="flex"
        data-hook={HIGHLIGHTED_DEALS_DESKTOP_MARKER.hook}
      >
        {childrenArray.map((child, index) => (
          <Col span={12} key={index}>
            {child}
          </Col>
        ))}
      </Row>
    );
  }

  return (
    <>
      <Carousel
        slideIndex={slideIndex}
        afterSlide={slideIndex => setSlideIndex(slideIndex)}
        wrapAround={true}
        autoplayInterval={cycleDelay}
        cellSpacing={10}
        data-hook={HIGHLIGHTED_DEALS_CAROUSEL_MARKER.hook}
      >
        {childrenArray.map((child, index) => {
          // Update slide index if slide has been focussed
          return (
            <div
              key={index}
              onFocus={() => goToSlide(index)}
              style={{ height: '100%' }}
            >
              {child}
            </div>
          );
        })}
      </Carousel>

      <IndicatorContainer>
        {childrenArray.map((_, index) => (
          <Indicator
            key={index}
            aria-label={`Go to slide ${index + 1}`}
            isActive={index === Math.ceil(slideIndex)}
            onClick={() => goToSlide(index)}
            {...hook(
              HIGHLIGHTED_DEALS_CAROUSEL_MARKER.hook,
              `slide-${index + 1}`
            )}
          />
        ))}
      </IndicatorContainer>
    </>
  );
};

HighlightedDeals.propTypes = propTypes;

export default HighlightedDeals;
